// Structured weekly delivery schedule helpers (shared by the menu editor,
// the public menu page display and the ordering restriction).
// days[] is indexed 0=Mon .. 6=Sun; each { open, from:'HH:MM', to:'HH:MM' }.

export const DAY_LABELS = ['Pirmdiena', 'Otrdiena', 'Trešdiena', 'Ceturtdiena', 'Piektdiena', 'Sestdiena', 'Svētdiena'];
export const DAY_SHORT = ['P.', 'O.', 'T.', 'C.', 'Pk.', 'S.', 'Sv.'];

export function emptyDeliveryHours() {
  return {
    enabled: false,
    days: Array.from({ length: 7 }, (_, i) => ({ open: i < 5, from: '10:00', to: '19:00' })),
  };
}

export function normalizeDeliveryHours(dh) {
  const base = emptyDeliveryHours();
  if (!dh || typeof dh !== 'object') return base;
  const days = Array.isArray(dh.days) ? dh.days : [];
  return {
    enabled: !!dh.enabled,
    days: base.days.map((d, i) => ({ ...d, ...(days[i] || {}) })),
  };
}

// Current weekday (0=Mon..6=Sun) and minutes-since-midnight in Europe/Riga.
function rigaNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Riga', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const wd = map[get('weekday')] ?? 0;
  let hh = parseInt(get('hour'), 10);
  if (get('hour') === '24') hh = 0;  // some engines emit '24' at midnight
  const mm = parseInt(get('minute'), 10);
  return { wd, minutes: hh * 60 + mm };
}

// { restricted, openNow, today } — restricted=false means "no schedule / always open".
export function deliveryStatus(dh) {
  if (!dh || !dh.enabled || !Array.isArray(dh.days)) return { restricted: false, openNow: true, today: null };
  const { wd, minutes } = rigaNow();
  const d = dh.days[wd];
  if (!d || !d.open || !d.from || !d.to) return { restricted: true, openNow: false, today: d || null };
  const [fh, fm] = String(d.from).split(':').map(Number);
  const [th, tm] = String(d.to).split(':').map(Number);
  const openNow = minutes >= (fh * 60 + fm) && minutes <= (th * 60 + tm);
  return { restricted: true, openNow, today: d };
}

// Group consecutive days with identical windows into readable rows, e.g.
// [{ days:'P.–Pk.', text:'10:00–19:00' }, { days:'S.–Sv.', text:'11:00–17:00' }].
export function formatDeliveryHours(dh) {
  if (!dh || !dh.enabled || !Array.isArray(dh.days)) return [];
  const sig = (d) => (d && d.open && d.from && d.to ? `${d.from}-${d.to}` : 'closed');
  const rows = [];
  let start = 0;
  for (let i = 1; i <= 7; i++) {
    if (i === 7 || sig(dh.days[i]) !== sig(dh.days[start])) {
      const d = dh.days[start];
      const label = start === i - 1 ? DAY_SHORT[start] : `${DAY_SHORT[start]}–${DAY_SHORT[i - 1]}`;
      const text = sig(d) === 'closed' ? 'Slēgts' : `${d.from}–${d.to}`;
      rows.push({ days: label, text });
      start = i;
    }
  }
  return rows;
}

// When does delivery next open/close? Returns null when unrestricted.
// { openNow, minsUntil, at:'HH:MM', day:0..6 } — minsUntil counts to the close
// (if open now) or to the next opening (if closed). All in Europe/Riga time.
export function deliveryCountdown(dh) {
  const st = deliveryStatus(dh);
  if (!st.restricted) return null;
  const { wd, minutes } = rigaNow();
  const nowMOW = wd * 1440 + minutes;
  if (st.openNow && st.today?.to) {
    const [th, tm] = String(st.today.to).split(':').map(Number);
    const closeMOW = wd * 1440 + th * 60 + tm;
    return { openNow: true, minsUntil: Math.max(0, closeMOW - nowMOW), at: st.today.to, day: wd };
  }
  let best = null;
  for (let off = 0; off < 7; off++) {
    const di = (wd + off) % 7;
    const d = dh.days[di];
    if (!d || !d.open || !d.from) continue;
    const [fh, fm] = String(d.from).split(':').map(Number);
    const openMOW = di * 1440 + fh * 60 + fm;
    let delta = (openMOW - nowMOW + 10080) % 10080;
    if (delta === 0) delta = 10080;
    if (best === null || delta < best.delta) best = { delta, day: di, at: d.from };
  }
  if (!best) return { openNow: false, minsUntil: null, at: null, day: null };
  return { openNow: false, minsUntil: best.delta, at: best.at, day: best.day };
}

// Human countdown within a day; longer gaps should show the weekday instead.
export function countdownLabel(mins) {
  if (mins == null || mins < 0) return '';
  if (mins < 1) return 'drīz';
  if (mins < 60) return `pēc ${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  if (mins < 1440) return m ? `pēc ${h} st ${m} min` : `pēc ${h} st`;
  return '';
}

// Readable "next opening" label for a schedule, e.g. "plkst. 10:00" or
// "Pk. plkst. 10:00" when the next opening is on a different weekday.
// Returns '' when unrestricted or already open.
export function nextOpenLabel(dh) {
  const cd = deliveryCountdown(dh);
  if (!cd || cd.openNow || !cd.at) return '';
  const { wd } = rigaNow();
  const prefix = cd.day != null && cd.day !== wd ? `${DAY_SHORT[cd.day]} ` : '';
  return `${prefix}plkst. ${cd.at}`;
}
